import hljs from 'highlight.js/lib/core';
import React, { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { SegmentedControl, SegmentedControlVariant, ToggleHeight, formatNumber } from '@column/column-ui-kit';

import { CopyRow } from '~/app/pages/Transfers/_components/CopyRow';
import { Card } from '~/components';
import { formatDateTime, isValidDate, keysToSnakeCase } from '~/util';

interface DetailsProps {
  formattedData?: Record<string, unknown>;
  rawData?: Record<string, unknown>;
  children?: React.ReactNode;
  label?: string;
  className?: string;
  /** Force the label-over-card stacked layout regardless of container width (e.g. in a narrow sheet). */
  stacked?: boolean;
}

// Establishes the query container so the label/card layout below responds to the width it's actually
// placed in (a full page vs. a narrow sheet), not the viewport.
const Wrapper = styled.div`
  container-type: inline-size;
  container-name: details;
  padding: 0 var(--page-margin) 24px;
`;

// Label beside the card once the container is wide enough, else stacked (label on top). The threshold is a
// container width; 520px is where a label + card reads comfortably, close to the old viewport-tablet break
// so wider pages are unchanged. (Narrow panels can force the stacked layout via the `stacked` prop.)
const COLUMN_BREAKPOINT = '520px';

const Layout = styled.div<{ $stacked?: boolean }>`
  display: grid;
  grid-gap: 24px;
  align-items: start;

  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-row-gap: 16px;

  > *:nth-child(3) {
    grid-column: 1 / -1;
  }

  /* $stacked pins the single-column layout (label over card) regardless of container width. */
  ${({ $stacked }) =>
    !$stacked &&
    css`
      @container details (min-width: ${COLUMN_BREAKPOINT}) {
        grid-template-columns: minmax(180px, 25%) 1fr;
        grid-template-rows: auto auto;
        grid-row-gap: 0px;

        > *:nth-child(3) {
          grid-column: 1 / -1;
        }
      }

      @container details (min-width: ${({ theme }) => theme.containerBreakpoints.wide}) {
        grid-template-columns: minmax(180px, 25%) auto 25%;
        grid-template-rows: auto;
        grid-row-gap: 0px;

        > *:nth-child(3) {
          grid-column: auto;
        }
      }
    `}
`;

const Title = styled.div`
  line-height: 1;
  display: grid;
  grid-gap: 4px;
`;

const Label = styled.div`
  margin-top: 12px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const StyledCard = styled(Card)`
  min-width: 248px;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  width: 100%;
  padding: 0 16px;
  overflow: hidden;

  > button {
    padding: 12px 0px;
  }
`;

const CopyRowList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0 1px;
  margin: 0;
  gap: 0;
  container-type: inline-size;
  container-name: details-row;

  li {
    border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

    &:first-child {
      border-top: none;
    }
  }

  ul {
    background: ${({ theme }) => theme.secondary.blendToBackground(25)};
    border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(300)};
    margin: 16px 0px 0;
    padding: 0 1px;

    @container details-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
      margin: -1px 0px 0;
    }

    .copy-row {
      padding-left: 8px;
    }
  }

  .copy-row label {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const Code = styled.pre`
  background: ${({ theme }) => theme.secondary.blendToBackground(25)};
  border-radius: 0 0 8px 8px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.6;
  box-sizing: border-box;
  margin: 0 1px;

  .hljs {
    padding: 16px;
    font-family: 'Roboto Mono', monospace;
    white-space: pre;
    background: transparent;

    min-width: 100%;
    width: 0px;
    overflow: auto;
  }
`;

const formatLabel = (content: string): string => {
  const exceptions: { [key: string]: string } = {
    id: 'ID',
    iat: 'IAT',
    micr: 'MICR',
    fi: 'FI',
    di: 'DI',
    imad: 'IMAD',
    uetr: 'UETR',
    fx: 'FX',
    fis: 'FIS',
    omad: 'OMAD',
    line1: 'Line 1',
    line2: 'Line 2',
    line3: 'Line 3',
    line4: 'Line 4',
    line5: 'Line 5',
    line6: 'Line 6',
  };

  const words = content
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return exceptions[word] || word;
    });

  return words
    .join(' ')
    .replace(/\b\w/g, (s) => s.toUpperCase())
    .trim();
};

const isMonospaced = (label: string): boolean => {
  const formattedLabel = formatLabel(label);
  return (
    formattedLabel.includes('ID') ||
    formattedLabel.includes('UETR') ||
    formattedLabel.includes('Idempotency Key') ||
    formattedLabel.includes('Image')
  );
};

const TransferObject = (obj: Record<string, any>, parent?: string) => {
  const isAddressObject = (addressObj: Record<string, any>) => {
    const addressFields = ['line1', 'city', 'state', 'postalCode', 'countryCode'];
    return addressFields.every((field) => field in addressObj);
  };

  if (isAddressObject(obj)) {
    const addressFields = [
      { key: 'line1', label: 'Line 1' },
      { key: 'line2', label: 'Line 2' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'postalCode', label: 'Postal Code' },
      { key: 'countryCode', label: 'Country Code' },
    ];

    return addressFields
      .filter(({ key }) => obj[key] !== undefined)
      .map(({ key, label }) => (
        <li key={parent ? `${key}-${parent}` : key}>
          <CopyRow label={label} value={String(obj[key] ?? '')} isMonospaced={false} className="copy-row" />
        </li>
      ));
  }

  return Object.keys(obj)
    .sort()
    .map((key) => {
      if (Array.isArray(obj[key])) {
        if (obj[key].length === 0) {
          return (
            <li key={parent ? `${key}-${parent}` : key}>
              <CopyRow label={formatLabel(key)} value="[]" isMonospaced={isMonospaced(key)} className="copy-row" />
            </li>
          );
        }
        return (
          <li key={parent ? `${key}-${parent}` : key}>
            <CopyRow
              label={formatLabel(key)}
              value={null}
              isParent
              isMonospaced={isMonospaced(key)}
              className="copy-row"
            />
            <CopyRowList>
              {obj[key].map((item: any, index: number) => (
                <li key={`${key}-${index}`}>
                  {typeof item === 'object' && item !== null ? (
                    <CopyRowList>{TransferObject(item, `${key}-${index}`)}</CopyRowList>
                  ) : (
                    <CopyRow label="" value={String(item)} isMonospaced={isMonospaced(key)} className="copy-row" />
                  )}
                </li>
              ))}
            </CopyRowList>
          </li>
        );
      } else if (isValidDate(obj[key])) {
        return (
          <li key={parent ? `${key}-${parent}` : key}>
            <CopyRow
              label={formatLabel(key)}
              value={formatDateTime(obj[key], { timeZoneFormat: 'utc' })}
              isMonospaced={isMonospaced(key)}
              className="copy-row"
            />
          </li>
        );
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (Object.keys(obj[key]).length === 0) {
          return (
            <li key={parent ? `${key}-${parent}` : key}>
              <CopyRow label={formatLabel(key)} value="{}" isMonospaced={isMonospaced(key)} className="copy-row" />
            </li>
          );
        }

        return (
          <li key={parent ? `${key}-${parent}` : key}>
            <CopyRow
              label={formatLabel(key)}
              value={null}
              isParent
              isMonospaced={isMonospaced(key)}
              className="copy-row"
            />
            <CopyRowList>{TransferObject(obj[key], key)}</CopyRowList>
          </li>
        );
      } else if (typeof obj[key] === 'boolean') {
        return (
          <li key={parent ? `${key}-${parent}` : key}>
            <CopyRow
              label={formatLabel(key)}
              value={obj[key].toString()}
              isMonospaced={isMonospaced(key)}
              className="copy-row"
            />
          </li>
        );
      } else {
        const value = obj[key] ?? '-';
        const formattedValue =
          typeof value === 'number'
            ? key.toLowerCase() === 'amount'
              ? formatNumber(value, obj.currencyCode)
              : key.toLowerCase().endsWith('amount')
                ? formatNumber(value, obj[`${key.split('Amount')[0]}CurrencyCode`])
                : value.toString()
            : value;
        return (
          <li key={parent ? `${key}-${parent}` : key}>
            <CopyRow
              label={formatLabel(key)}
              value={formattedValue}
              isMonospaced={isMonospaced(key)}
              className="copy-row"
            />
          </li>
        );
      }
    });
};

export const Details: FC<DetailsProps> = ({
  formattedData,
  rawData,
  children,
  label = 'Details',
  className,
  stacked,
}) => {
  const [format, setFormat] = useState<'formatted' | 'json'>('formatted');

  useEffect(() => {
    hljs.highlightAll();
  }, [format, rawData]);

  return (
    <Wrapper className={className}>
      <Layout $stacked={stacked}>
        <Title>
          <Label>{label}</Label>
        </Title>
        <StyledCard>
          <StyledSegmentedControl
            options={[
              { label: 'Formatted', value: 'formatted' },
              { label: 'JSON', value: 'json' },
            ]}
            active={format}
            onOptionChange={setFormat}
            variant={SegmentedControlVariant.Line}
          />
          <ToggleHeight isClose={format !== 'formatted'}>
            <CopyRowList>{formattedData ? TransferObject(formattedData) : children}</CopyRowList>
          </ToggleHeight>
          <ToggleHeight isClose={format !== 'json'}>
            <Code>
              <code className="language-json">{JSON.stringify(keysToSnakeCase(rawData), null, 2)}</code>
            </Code>
          </ToggleHeight>
        </StyledCard>
      </Layout>
    </Wrapper>
  );
};
